import React, { useState, useEffect, useCallback } from 'react';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Loader2, Info, Check, AlertCircle, FileText } from 'lucide-react';

/**
 * GST & HSN/SAC Auto-Suggestion Component
 * 
 * Automatically fetches and suggests:
 * - GST rate based on category, subcategory and price (for threshold-based slabs)
 * - HSN/SAC code based on category and subcategory
 * 
 * Props:
 * - categoryId: string - The category ID
 * - subcategoryId: string - The subcategory ID (from subcategory object)
 * - subcategoryName: string - Subcategory name for display
 * - price: number - Current price (used for slab calculation)
 * - currentTaxRate: string/number - Current tax rate value in form
 * - onTaxRateChange: function - Callback to update tax rate in parent form
 * - currentHsnCode: string - Current HSN/SAC code value in form
 * - onHsnCodeChange: function - Callback to update HSN/SAC code in parent form
 * - chargeTax: boolean - Whether tax charging is enabled
 * - listingType: string - 'product' or 'service'
 */
const GstAutoSuggestion = ({
  categoryId,
  subcategoryId,
  subcategoryName,
  price,
  currentTaxRate,
  onTaxRateChange,
  currentHsnCode,
  onHsnCodeChange,
  chargeTax,
  listingType = 'product'
}) => {
  const [loading, setLoading] = useState(false);
  const [gstData, setGstData] = useState(null);
  const [hsnData, setHsnData] = useState(null);
  const [error, setError] = useState(null);
  const [isGstApplied, setIsGstApplied] = useState(false);
  const [isHsnApplied, setIsHsnApplied] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch GST rate and HSN/SAC code when category, subcategory, or price changes
  const fetchSuggestions = useCallback(async () => {
    if (!categoryId || !subcategoryId) {
      setGstData(null);
      setHsnData(null);
      return;
    }

    // Use a reasonable default price for initial suggestion
    const priceValue = parseFloat(price) || 0;

    try {
      setLoading(true);
      setError(null);

      const token = sessionStorage.getItem('token');
      
      // Fetch both GST rate and HSN/SAC code in parallel
      const [gstResponse, hsnResponse] = await Promise.all([
        fetch(
          `${BACKEND_URL}/api/categories/${categoryId}/gst-rate?subcategory_id=${subcategoryId}&price=${priceValue}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        ),
        fetch(
          `${BACKEND_URL}/api/categories/${categoryId}/hsn-sac-suggestion?subcategory_id=${subcategoryId}&listing_type=${listingType}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        )
      ]);

      // Process GST response
      if (gstResponse.ok) {
        const gstResult = await gstResponse.json();
        setGstData(gstResult);
        
        // Check if current tax rate matches suggested rate
        const currentRate = parseFloat(currentTaxRate) || 0;
        setIsGstApplied(currentRate === gstResult.gst_rate);
      }

      // Process HSN/SAC response
      if (hsnResponse.ok) {
        const hsnResult = await hsnResponse.json();
        setHsnData(hsnResult);
        
        // Check if current HSN code matches suggested code
        const suggestedCode = listingType === 'product' ? hsnResult.hsn_code : hsnResult.sac_code;
        setIsHsnApplied(currentHsnCode === suggestedCode);
      }

    } catch (err) {
      console.error('Suggestion fetch error:', err);
      setError(err.message);
      setGstData(null);
      setHsnData(null);
    } finally {
      setLoading(false);
    }
  }, [categoryId, subcategoryId, price, BACKEND_URL, currentTaxRate, currentHsnCode, listingType]);

  // Debounced effect to fetch suggestions
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSuggestions();
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [fetchSuggestions]);

  // Update applied states when values change externally
  useEffect(() => {
    if (gstData) {
      const currentRate = parseFloat(currentTaxRate) || 0;
      setIsGstApplied(currentRate === gstData.gst_rate);
    }
  }, [currentTaxRate, gstData]);

  useEffect(() => {
    if (hsnData) {
      const suggestedCode = listingType === 'product' ? hsnData.hsn_code : hsnData.sac_code;
      setIsHsnApplied(currentHsnCode === suggestedCode);
    }
  }, [currentHsnCode, hsnData, listingType]);

  // Handle apply suggested GST rate
  const handleApplyGstRate = () => {
    if (gstData && onTaxRateChange) {
      onTaxRateChange(gstData.gst_rate.toString());
      setIsGstApplied(true);
    }
  };

  // Handle apply suggested HSN/SAC code
  const handleApplyHsnCode = () => {
    if (hsnData && onHsnCodeChange) {
      const suggestedCode = listingType === 'product' ? hsnData.hsn_code : hsnData.sac_code;
      if (suggestedCode) {
        onHsnCodeChange(suggestedCode);
        setIsHsnApplied(true);
      }
    }
  };

  // Apply both suggestions at once
  const handleApplyAll = () => {
    handleApplyGstRate();
    handleApplyHsnCode();
  };

  // Don't show if tax is not being charged
  if (!chargeTax) {
    return null;
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-2 bg-muted/30 rounded-md">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Fetching tax suggestions...</span>
      </div>
    );
  }

  // No subcategory selected
  if (!subcategoryId) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-2 bg-muted/30 rounded-md">
        <Info className="h-4 w-4" />
        <span>Select a subcategory to get GST rate and {listingType === 'product' ? 'HSN' : 'SAC'} code suggestions</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert variant="destructive" className="py-2">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription className="text-xs">
          Unable to fetch suggestions: {error}
        </AlertDescription>
      </Alert>
    );
  }

  // No data available
  if (!gstData && !hsnData) {
    return null;
  }

  const suggestedHsnCode = hsnData ? (listingType === 'product' ? hsnData.hsn_code : hsnData.sac_code) : null;
  const hsnLabel = listingType === 'product' ? 'HSN Code' : 'SAC Code';
  const allApplied = isGstApplied && (isHsnApplied || !suggestedHsnCode);
  const hasBothSuggestions = gstData && suggestedHsnCode;

  // Render suggestions
  return (
    <div className="space-y-3 mt-3">
      {/* Header with Apply All button */}
      {hasBothSuggestions && !allApplied && (
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Tax Suggestions</span>
          <Button
            type="button"
            size="sm"
            onClick={handleApplyAll}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Apply All
          </Button>
        </div>
      )}

      {/* HSN/SAC Code Suggestion */}
      {suggestedHsnCode && (
        <div className={`flex items-center justify-between p-3 rounded-lg border ${
          isHsnApplied ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'
        }`}>
          <div className="flex items-center gap-3">
            {isHsnApplied ? (
              <Check className="h-5 w-5 text-green-600" />
            ) : (
              <FileText className="h-5 w-5 text-amber-600" />
            )}
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">
                  Suggested {hsnLabel}:
                </span>
                <Badge variant={isHsnApplied ? 'default' : 'secondary'} className="text-sm font-mono">
                  {suggestedHsnCode}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                From {hsnData.source === 'subcategory' ? subcategoryName || hsnData.subcategory_name : hsnData.category_name}
              </p>
            </div>
          </div>

          {!isHsnApplied && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleApplyHsnCode}
              className="text-amber-600 border-amber-300 hover:bg-amber-100"
            >
              Apply
            </Button>
          )}
        </div>
      )}

      {/* GST Rate Suggestion */}
      {gstData && (
        <div className={`flex items-center justify-between p-3 rounded-lg border ${
          isGstApplied ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'
        }`}>
          <div className="flex items-center gap-3">
            {isGstApplied ? (
              <Check className="h-5 w-5 text-green-600" />
            ) : (
              <Info className="h-5 w-5 text-blue-600" />
            )}
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">
                  Suggested GST Rate:
                </span>
                <Badge variant={isGstApplied ? 'default' : 'secondary'} className="text-sm">
                  {gstData.gst_rate}%
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Based on {subcategoryName || gstData.subcategory_name}
                {gstData.rate_type === 'threshold_based' && price > 0 && (
                  <span> at ₹{parseFloat(price).toLocaleString('en-IN')}</span>
                )}
              </p>
            </div>
          </div>

          {!isGstApplied && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleApplyGstRate}
              className="text-blue-600 border-blue-300 hover:bg-blue-100"
            >
              Apply
            </Button>
          )}
        </div>
      )}

      {/* Threshold-based slabs info */}
      {gstData?.rate_type === 'threshold_based' && gstData.all_slabs && gstData.all_slabs.length > 0 && (
        <div className="p-2 bg-gray-50 rounded-md">
          <p className="text-xs font-medium text-gray-700 mb-1">
            GST Rate Slabs for this subcategory:
          </p>
          <div className="flex flex-wrap gap-1">
            {gstData.all_slabs.map((slab, index) => {
              const prevThreshold = index > 0 ? gstData.all_slabs[index - 1].threshold : 0;
              const isActive = gstData.slab_info?.slab_index === index + 1;
              
              return (
                <Badge 
                  key={slab.id || slab.name || `${slab.title || "slab"}-${index}`} 
                  variant={isActive ? 'default' : 'outline'}
                  className={`text-xs ${isActive ? 'bg-blue-600' : ''}`}
                >
                  {index === 0 ? 'Up to' : `₹${prevThreshold.toLocaleString('en-IN')} -`} ₹{slab.threshold.toLocaleString('en-IN')}: {slab.rate}%
                </Badge>
              );
            })}
            {gstData.slab_info?.max_price === null && (
              <Badge variant="outline" className="text-xs">
                Above ₹{gstData.all_slabs[gstData.all_slabs.length - 1]?.threshold.toLocaleString('en-IN')}: {gstData.gst_rate}%
              </Badge>
            )}
          </div>
        </div>
      )}

      {/* All Applied confirmation */}
      {allApplied && (
        <p className="text-xs text-green-600 flex items-center gap-1">
          <Check className="h-3 w-3" />
          All tax details applied successfully
        </p>
      )}
    </div>
  );
};

export default GstAutoSuggestion;
